/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

/**
 * InstallAppBanner – a dismissible banner prompting users to install the PWA.
 *
 * Shows automatically when the browser fires `beforeinstallprompt`.
 * Hides once installed or dismissed (remembers dismissal for 7 days).
 *
 * Usage:
 *   import InstallAppBanner from '@/components/InstallAppBanner';
 *   // Place near the top of your layout:
 *   <InstallAppBanner />
 */

import React, { useState, useEffect } from 'react';
import useInstallPrompt from '../hooks/useInstallPrompt';

const DISMISS_KEY = 'pwa-install-dismissed';
const DISMISS_DAYS = 7;

export default function InstallAppBanner() {
  const { canInstall, isInstalled, install } = useInstallPrompt();
  const [dismissed, setDismissed] = useState(true); // start hidden

  useEffect(() => {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (raw) {
      const expiry = parseInt(raw, 10);
      if (Date.now() < expiry) {
        setDismissed(true);
        return;
      }
    }
    setDismissed(false);
  }, []);

  if (isInstalled || !canInstall || dismissed) return null;

  const handleDismiss = () => {
    const expiry = Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000;
    localStorage.setItem(DISMISS_KEY, String(expiry));
    setDismissed(true);
  };

  const handleInstall = async () => {
    const accepted = await install();
    if (!accepted) {
      // User declined — treat like dismiss
      handleDismiss();
    }
  };

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 mx-auto max-w-lg animate-slide-up">
      <div className="flex items-center gap-3 rounded-xl border border-blue-500/30 bg-gray-900/95 px-4 py-3 shadow-2xl backdrop-blur-md">
        {/* App icon */}
        <img
          src="/icons/icon-96x96.png"
          alt="paxia.ai"
          className="h-10 w-10 rounded-lg"
        />

        {/* Text */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-white truncate">
            Install paxia.ai
          </p>
          <p className="text-xs text-gray-400">
            Add to home screen for quick access & offline support
          </p>
        </div>

        {/* Install button */}
        <button
          onClick={handleInstall}
          className="shrink-0 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white 
                     hover:bg-blue-500 active:bg-blue-700 transition-colors"
        >
          Install
        </button>

        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="shrink-0 p-1 text-gray-400 hover:text-white transition-colors"
          aria-label="Dismiss"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}
